import {
  ORCHESTRATION_WS_METHODS,
  type EnvironmentId as EnvironmentIdType,
  type OrchestrationThread,
  type OrchestrationThreadHistoryPage,
  type OrchestrationThreadHistoryWindow,
  type OrchestrationThreadStreamItem,
  type ThreadId as ThreadIdType,
} from "@afar-research/contracts";
import {
  THREAD_HISTORY_CLIENT_WINDOW_MAX_DECODED_BYTES,
  THREAD_HISTORY_CLIENT_WINDOW_MAX_MESSAGE_COUNT,
} from "@afar-research/shared/threadHistory";
import * as Cause from "effect/Cause";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Queue from "effect/Queue";
import * as Ref from "effect/Ref";
import * as Semaphore from "effect/Semaphore";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { clientReadiness, type ClientReadiness } from "../connection/model.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import {
  clientTransportEnvironmentOwner,
  ClientTransportResources,
  forkClientTransportSubscription,
} from "../connection/resources.ts";
import { request, subscribe } from "../rpc/client.ts";
import { parseThreadKey, threadKey } from "./entities.ts";
import { applyThreadDetailEvent, threadFromCreatedEvent } from "./threadReducer.ts";
import { mergeOlderThreadHistoryPage } from "./threadHistory.ts";
import { THREAD_SUBSCRIPTION_IDLE_TTL_MS } from "./threadRetention.ts";
import { followStreamInEnvironment } from "./runtime.ts";

export type EnvironmentThreadStatus =
  | "unavailable"
  | "starting"
  | "synchronizing"
  | "ready"
  | "deleted";

export interface EnvironmentThreadState {
  readonly data: Option.Option<OrchestrationThread>;
  readonly status: EnvironmentThreadStatus;
  readonly error: Option.Option<string>;
  readonly history?: OrchestrationThreadHistoryWindow;
  readonly historyRevision?: number;
  /** True while an older page is in flight, including one parked awaiting the stream. */
  readonly loadingOlder?: boolean;
  /** Why the last older-page request could not be served, for the row that offers a retry. */
  readonly olderError?: string;
}

export const EMPTY_ENVIRONMENT_THREAD_STATE: EnvironmentThreadState = {
  data: Option.none(),
  status: "unavailable",
  error: Option.none(),
  historyRevision: 0,
};

/**
 * Apply a connection readiness change to one thread.
 *
 * Readiness alone never discards what the thread said. The last known thread stays
 * readable, marked stale by the new status, until the subscription's next snapshot
 * replaces it. This mirrors `applyShellReadiness`, so a thread and the shell that lists
 * it never disagree about what the client still knows; a thread the server withdrew stays
 * `deleted`, because that authority did not come from the connection.
 *
 * What does not survive is anything that only means something to the connection that is
 * gone: the history window's cursor and a pending request to refetch with it both name a
 * stream the Environment is no longer serving, and the snapshot that ends the outage
 * issues its own. Keeping them would let a stale cursor reach a reconciled thread.
 *
 * Only `ready` is authoritative. Everything else means the retained thread may already be
 * behind the Environment, which is why `applyItem` waits for a snapshot before it reduces
 * another event onto it.
 */
export function applyThreadReadiness(
  current: EnvironmentThreadState,
  readiness: ClientReadiness,
): EnvironmentThreadState {
  if (current.status === "deleted") {
    return current;
  }
  if (readiness === "ready") {
    // A live thread stays live: readiness republishes on unrelated supervisor changes,
    // and demoting an authoritative thread would stall it until the next snapshot.
    return current.status === "ready"
      ? current
      : { ...current, status: "synchronizing", error: Option.none() };
  }
  if (
    current.status === readiness &&
    Option.isNone(current.error) &&
    current.history === undefined &&
    current.historyRevision === 0
  ) {
    return current;
  }
  return {
    data: current.data,
    status: readiness,
    error: Option.none(),
    historyRevision: 0,
  };
}

function formatThreadError(cause: Cause.Cause<unknown>): string {
  const error = Cause.squash(cause);
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : "Could not synchronize the thread.";
}

/**
 * Live older-page loaders, one per subscribed thread. Registered by the state
 * machine and dropped when its scope closes, so a request for a thread nothing
 * holds is a no-op rather than an unguarded fetch.
 */
const liveOlderTurnLoaders = new Map<string, () => void>();

/**
 * Ask the live machine for `threadId` to load its next older page. Returns
 * false when no machine holds the thread; callers render from
 * `EnvironmentThreadState` and can treat that as nothing to do.
 */
export function requestOlderThreadTurns(
  environmentId: EnvironmentIdType,
  threadId: ThreadIdType,
): boolean {
  const load = liveOlderTurnLoaders.get(threadKey({ environmentId, threadId }));
  if (load === undefined) return false;
  load();
  return true;
}

export const makeEnvironmentThreadState = Effect.fn("EnvironmentThreadState.make")(function* (
  threadId: ThreadIdType,
) {
  const supervisor = yield* EnvironmentSupervisor;
  const resources = yield* ClientTransportResources;
  const ownerIdentity = clientTransportEnvironmentOwner(supervisor.target.environmentId);
  const state = yield* SubscriptionRef.make<EnvironmentThreadState>(EMPTY_ENVIRONMENT_THREAD_STATE);
  const lastSequence = yield* SubscriptionRef.make(0);
  const environmentId = supervisor.target.environmentId;

  /**
   * Retain what the client knows and mark it stale. The sequence watermark resets with
   * it: the next authoritative snapshot renumbers the stream it belongs to.
   */
  const applyReadiness = (readiness: ClientReadiness) =>
    SubscriptionRef.update(state, (current) => applyThreadReadiness(current, readiness)).pipe(
      Effect.andThen(readiness === "ready" ? Effect.void : SubscriptionRef.set(lastSequence, 0)),
    );
  const setStreamError = (cause: Cause.Cause<unknown>) =>
    SubscriptionRef.update(state, (current) => ({
      ...current,
      error: Option.some(formatThreadError(cause)),
    }));

  const setThread = Effect.fn("EnvironmentThreadState.setThread")(function* (
    thread: OrchestrationThread,
    history?: OrchestrationThreadHistoryWindow,
    invalidateHistory = false,
  ) {
    const current = yield* SubscriptionRef.get(state);
    const nextHistory = history ?? current.history;
    yield* SubscriptionRef.set(state, {
      data: Option.some(thread),
      status: "ready",
      error: Option.none(),
      historyRevision: invalidateHistory
        ? (current.historyRevision ?? 0) + 1
        : (current.historyRevision ?? 0),
      ...(nextHistory === undefined ? {} : { history: nextHistory }),
    });
  });

  const setDeleted = Effect.fn("EnvironmentThreadState.setDeleted")(function* () {
    yield* Ref.update(historyEpoch, (epoch) => epoch + 1);
    yield* Ref.set(pendingOlderPage, null);
    yield* SubscriptionRef.set(state, {
      data: Option.none(),
      status: "deleted",
      error: Option.none(),
      historyRevision: 0,
    });
  });

  /**
   * Bumped whenever loaded history may have been rewritten out from under an
   * in-flight older-page fetch: a replacement snapshot, a Checkpoint Revert,
   * or a deletion. A page captured under an older epoch is discarded, never
   * merged — the alternative is resurrecting turns the rewrite removed.
   */
  const historyEpoch = yield* Ref.make(0);
  /**
   * Serializes stream-item application against the older-page staleness check
   * and its merge. Without it a rewrite could land between the check and the
   * merge and still slip resurrected history in.
   */
  const applyLock = yield* Semaphore.make(1);
  /**
   * A page read ahead of the live state, parked until the subscription catches
   * up. At most one can exist, because a fetch is refused while one is loading.
   */
  const pendingOlderPage = yield* Ref.make<{
    readonly page: OrchestrationThreadHistoryPage;
    readonly epoch: number;
  } | null>(null);

  /** A new attempt clears the previous failure rather than layering onto it. */
  const setLoadingOlder = (loadingOlder: boolean, olderError?: string) =>
    SubscriptionRef.update(state, (current) => {
      const { olderError: _cleared, ...rest } = current;
      return {
        ...rest,
        loadingOlder,
        ...(olderError === undefined ? {} : { olderError }),
      };
    });

  /** Merge a page beneath the live window and adopt its older cursor. */
  const mergeOlderPage = Effect.fn("EnvironmentThreadState.mergeOlderPage")(function* (
    page: OrchestrationThreadHistoryPage,
  ) {
    yield* SubscriptionRef.update(state, (current) =>
      Option.match(current.data, {
        onNone: () => current,
        onSome: (thread) => {
          const { olderError: _cleared, ...rest } = current;
          return {
            ...rest,
            data: Option.some(mergeOlderThreadHistoryPage(thread, page)),
            history: page.history,
            loadingOlder: false,
          };
        },
      }),
    );
  });

  /**
   * Merge a parked page once the live state has reached it. Runs under
   * applyLock, and discards the page if the epoch moved while it waited.
   */
  const drainPendingOlderPage = Effect.fn("EnvironmentThreadState.drainPendingOlderPage")(
    function* () {
      const pending = yield* Ref.get(pendingOlderPage);
      if (pending === null) return;
      const epochNow = yield* Ref.get(historyEpoch);
      if (epochNow !== pending.epoch) {
        yield* Ref.set(pendingOlderPage, null);
        yield* setLoadingOlder(false);
        return;
      }
      const loaded = yield* SubscriptionRef.get(lastSequence);
      const watermark = pending.page.snapshotSequence;
      if (watermark !== undefined && watermark > loaded) return;
      yield* Ref.set(pendingOlderPage, null);
      yield* mergeOlderPage(pending.page);
    },
  );

  const loadOlderTurns = Effect.fn("EnvironmentThreadState.loadOlderTurns")(function* () {
    const current = yield* SubscriptionRef.get(state);
    const cursor = current.history?.olderCursor ?? null;
    if (cursor === null || current.loadingOlder === true) return;

    const epochAtStart = yield* Ref.get(historyEpoch);
    yield* setLoadingOlder(true);
    const page = yield* request(ORCHESTRATION_WS_METHODS.getThreadHistoryPage, {
      threadId,
      cursor,
      maxDecodedBytes: THREAD_HISTORY_CLIENT_WINDOW_MAX_DECODED_BYTES,
      maxMessageCount: THREAD_HISTORY_CLIENT_WINDOW_MAX_MESSAGE_COUNT,
    }).pipe(
      Effect.catch((cause) =>
        setLoadingOlder(false, formatThreadError(Cause.fail(cause))).pipe(Effect.as(null)),
      ),
    );
    if (page === null) return;

    // The staleness check and the merge share the lock with stream-item
    // application, so nothing that rewrites history can land between them:
    // anything that does bumps the epoch before this permit is acquired.
    yield* applyLock.withPermits(1)(
      Effect.gen(function* () {
        const epochNow = yield* Ref.get(historyEpoch);
        const loaded = yield* SubscriptionRef.get(lastSequence);
        const watermark = page.snapshotSequence;
        if (epochNow !== epochAtStart || (watermark !== undefined && watermark < loaded)) {
          yield* setLoadingOlder(false);
          return;
        }
        if (watermark !== undefined && watermark > loaded) {
          // loadingOlder stays true: the request is still outstanding from the
          // reader's point of view, and no second fetch should start.
          yield* Ref.set(pendingOlderPage, { page, epoch: epochNow });
          return;
        }
        yield* mergeOlderPage(page);
      }),
    );
  });

  // Body of applyItem; runs under applyLock.
  const applyItemLocked = Effect.fn("EnvironmentThreadState.applyItemLocked")(function* (
    item: OrchestrationThreadStreamItem,
  ) {
    if (clientReadiness(yield* SubscriptionRef.get(supervisor.state)) !== "ready") {
      return;
    }
    if (item.kind === "not-found") {
      yield* setDeleted();
      return;
    }
    if (item.kind === "snapshot") {
      // A fresh snapshot replaces all loaded history, including merged older
      // pages: a turn reverted while this client was away would otherwise
      // survive in them with no event left to remove it. The bump discards any
      // older-page fetch racing this snapshot, and a parked page must not
      // clear loadingOlder for a request started from the new cursor.
      yield* Ref.update(historyEpoch, (epoch) => epoch + 1);
      yield* Ref.set(pendingOlderPage, null);
      yield* SubscriptionRef.set(lastSequence, item.snapshot.snapshotSequence);
      yield* setThread(item.snapshot.thread, item.snapshot.history);
      // Whatever page was in flight or parked is no longer about this thread,
      // so the reader stops waiting on it.
      yield* setLoadingOlder(false);
      return;
    }
    if (item.kind === "ready") return;

    const sequence = yield* SubscriptionRef.get(lastSequence);
    if (item.event.sequence <= sequence) {
      return;
    }

    const current = yield* SubscriptionRef.get(state);
    /**
     * Only a `ready` thread is authoritative enough to reduce an event onto. Retained
     * data is stale by definition, so reducing here would fabricate a thread state that
     * never existed on the Environment; the snapshot that opens every subscription
     * carries the same events' effect anyway. Whether the thread exists at all is the
     * one thing worth learning early.
     */
    if (current.status !== "ready" || Option.isNone(current.data)) {
      if (item.event.type === "thread.created") {
        yield* SubscriptionRef.set(lastSequence, item.event.sequence);
        yield* setThread(threadFromCreatedEvent(item.event));
      } else if (item.event.type === "thread.deleted") {
        yield* SubscriptionRef.set(lastSequence, item.event.sequence);
        yield* setDeleted();
      }
      return;
    }
    yield* SubscriptionRef.set(lastSequence, item.event.sequence);
    if (item.event.type === "thread.reverted") {
      // The revert rewrites history the client has already loaded, so an
      // in-flight page for the old shape is no longer about this thread.
      yield* Ref.update(historyEpoch, (epoch) => epoch + 1);
      yield* Ref.set(pendingOlderPage, null);
      yield* setLoadingOlder(false);
    }
    const result = applyThreadDetailEvent(current.data.value, item.event);
    if (result.kind === "updated") {
      yield* setThread(result.thread, undefined, item.event.type === "thread.reverted");
    } else if (result.kind === "deleted") {
      yield* setDeleted();
    }
  });

  const applyItem = Effect.fn("EnvironmentThreadState.applyItem")(function* (
    item: OrchestrationThreadStreamItem,
  ) {
    yield* applyLock.withPermits(1)(
      applyItemLocked(item).pipe(Effect.andThen(drainPendingOlderPage())),
    );
  });

  yield* forkClientTransportSubscription(
    resources,
    {
      ownerIdentity,
      identity: `thread-state:${threadId}:readiness`,
      lifetime: "retained-owner",
    },
    SubscriptionRef.changes(supervisor.state).pipe(
      Stream.runForEach((connectionState) => applyReadiness(clientReadiness(connectionState))),
    ),
  );

  yield* forkClientTransportSubscription(
    resources,
    {
      ownerIdentity,
      identity: `thread-state:${threadId}:rpc`,
      lifetime: "retained-owner",
    },
    subscribe(
      ORCHESTRATION_WS_METHODS.subscribeThread,
      {
        threadId,
        history: {
          maxDecodedBytes: THREAD_HISTORY_CLIENT_WINDOW_MAX_DECODED_BYTES,
          maxMessageCount: THREAD_HISTORY_CLIENT_WINDOW_MAX_MESSAGE_COUNT,
        },
      },
      {
        onExpectedFailure: setStreamError,
        /**
         * Every failure is worth another attempt. A thread the client gives up on is a
         * thread the user cannot read, continue, or repair from inside the app, and the
         * Environment that refused it a moment ago is the same one that will serve it
         * after the next restart, migration, or reconnect. What that costs is bounded:
         * one request per half minute, only while something still holds the thread.
         */
        retryExpectedFailureAfter: (consecutiveFailureCount) =>
          Duration.millis(Math.min(250 * 2 ** consecutiveFailureCount, 30_000)),
      },
    ).pipe(Stream.runForEach(applyItem)),
  );

  // Requests cross into the machine through a queue so the fetch runs on the
  // machine's own fiber, with its Environment context and its lock, rather
  // than as an unguarded fetch beside it. Sliding(1): while a page is loading
  // the loader no-ops, so a queued second request would only ever be stale.
  const olderTurnRequests = yield* Queue.sliding<void>(1);
  yield* forkClientTransportSubscription(
    resources,
    {
      ownerIdentity,
      identity: `thread-state:${threadId}:older-turns`,
      lifetime: "retained-owner",
    },
    Stream.fromQueue(olderTurnRequests).pipe(Stream.runForEach(() => loadOlderTurns())),
  );
  const key = threadKey({ environmentId, threadId });
  liveOlderTurnLoaders.set(key, () => {
    Effect.runFork(Queue.offer(olderTurnRequests, undefined));
  });
  yield* Effect.addFinalizer(() =>
    Effect.sync(() => {
      liveOlderTurnLoaders.delete(key);
    }),
  );

  return state;
});

export function threadStateChanges(environmentId: EnvironmentIdType, threadId: ThreadIdType) {
  return followStreamInEnvironment(
    environmentId,
    Stream.unwrap(makeEnvironmentThreadState(threadId).pipe(Effect.map(SubscriptionRef.changes))),
  );
}

export function createEnvironmentThreadStateAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const family = Atom.family((key: string) => {
    const { environmentId, threadId } = parseThreadKey(key);
    return runtime
      .atom(threadStateChanges(environmentId, threadId), {
        initialValue: EMPTY_ENVIRONMENT_THREAD_STATE,
      })
      .pipe(
        Atom.setIdleTTL(THREAD_SUBSCRIPTION_IDLE_TTL_MS),
        Atom.withLabel(`environment-thread-state:${key}`),
      );
  });

  return {
    stateAtom: (environmentId: EnvironmentIdType, threadId: ThreadIdType) =>
      family(threadKey({ environmentId, threadId })),
  };
}

export * from "./archivedThreads.ts";
export * from "./checkpointDiff.ts";
export * from "./composerPathSearch.ts";
export * from "./threadCommands.ts";
export * from "./threadDetail.ts";
export * from "./threadReducer.ts";
export * from "./threadShell.ts";
export * from "./threadHistory.ts";
